/** Run-local salvage and demonstrated mastery policy. Costs are earned integer
 * tokens, never money. Critical-hit prerequisites cannot be bought with salvage. */
const paths = {
  weapon: { name: 'Cannon / Rail Lance', costs: [6, 10, 16, 24], criticalHits: [0, 0, 24, 60] },
  nova: { name: 'Nova / Starburst', costs: [6, 10, 18], criticalHits: [0, 0, 40] },
  booster: { name: 'Vector Boosters', costs: [5, 9, 14], criticalHits: [0, 0, 0] },
  shield: { name: 'Shield Capacitor', costs: [8, 14], criticalHits: [0, 0] },
  dual: { name: 'Dual Cannon', costs: [28], criticalHits: [0] },
  drone: {
    name: 'Escort Drones',
    costs: [18, 24, 32, 42, 54, 68, 84, 100],
    criticalHits: Array(8).fill(0),
  },
};
export const SKY_UPGRADE_PATHS = Object.freeze(
  Object.fromEntries(
    Object.entries(paths).map(([key, value]) => [
      key,
      Object.freeze({
        ...value,
        maxLevel: value.costs.length,
        costs: Object.freeze(value.costs),
        criticalHits: Object.freeze(value.criticalHits),
      }),
    ]),
  ),
);
const WEAPON_INTERVAL = Object.freeze([0.13, 0.11, 0.09, 0.07, 0.055]);
const WEAPON_DAMAGE = Object.freeze([1, 1.4, 1.9, 2.8, 3.8]);
const NOVA_DAMAGE = Object.freeze([12, 18, 26, 40]);
/** Resolve equipment units: intervals/durations in seconds, damage in HP and
 * lateral speed in metres/second. Invalid levels independently resolve to zero.
 * shotPierce is the extra-enemy budget available only on overdrive-fired bolts. */
export function skyEquipment(upgrades = {}) {
  const level = (key) => {
    const value =
      upgrades && typeof upgrades === 'object' && !Array.isArray(upgrades)
        ? upgrades[key]
        : undefined;
    return Number.isInteger(value) && value >= 0 && value <= SKY_UPGRADE_PATHS[key].maxLevel
      ? value
      : 0;
  };
  const weapon = level('weapon'),
    nova = level('nova'),
    booster = level('booster'),
    shield = level('shield');
  return {
    shotInterval: WEAPON_INTERVAL[weapon],
    shotCount: level('dual') ? 2 : 1,
    dualDamageScale: 0.8,
    droneCount: level('drone'),
    droneInterval: 1.15,
    droneDamage: 0.7,
    droneSpeed: 100,
    shotDamage: WEAPON_DAMAGE[weapon],
    shotSpeed: 120 + weapon * 20,
    aimCapture: [0.65, 0.85, 1.1, 1.35, 1.6][weapon],
    aimStrength: [0.15, 0.28, 0.42, 0.57, 0.72][weapon],
    novaDamage: NOVA_DAMAGE[nova],
    shotPierce: Math.max(0, weapon - 2),
    lateralSpeed: 10 + booster * 3,
    maxLives: 3 + shield,
    shieldGrace: 2 + shield * 0.35,
    overdriveSeconds: 4 + booster,
  };
}
/** Inspect without mutation. Only held intermissions can spend salvage. Invalid
 * state fails closed; mastery-locked offers retain cost and prerequisite for UI. */
export function skyUpgradeOffer(state, key) {
  if (key !== 'repair' && !Object.hasOwn(SKY_UPGRADE_PATHS, key))
    return { ok: false, reason: 'unknown-upgrade' };
  if (state?.status !== 'intermission') return { ok: false, reason: 'not-intermission' };
  if (!Number.isSafeInteger(state.salvage) || state.salvage < 0)
    return { ok: false, reason: 'invalid-salvage' };
  if (!state.upgrades || typeof state.upgrades !== 'object' || Array.isArray(state.upgrades))
    return { ok: false, reason: 'invalid-upgrades' };
  const maxLives = skyEquipment(state.upgrades).maxLives;
  if (key === 'repair') {
    if (!Number.isInteger(state.lives) || state.lives < 1 || state.lives >= maxLives)
      return { ok: false, reason: 'shields-full' };
    return state.salvage >= 3
      ? { ok: true, key, cost: 3 }
      : { ok: false, reason: 'insufficient-salvage', cost: 3 };
  }
  const path = SKY_UPGRADE_PATHS[key],
    level = state.upgrades?.[key];
  if (!Number.isInteger(level) || level < 0 || level > path.maxLevel)
    return { ok: false, reason: 'invalid-level' };
  if (level === path.maxLevel) return { ok: false, reason: 'max-level' };
  if (
    key === 'shield' &&
    (!Number.isInteger(state.lives) || state.lives < 1 || state.lives > maxLives)
  )
    return { ok: false, reason: 'invalid-shields' };
  const cost = path.costs[level],
    requiredCriticalHits = path.criticalHits[level];
  if (
    requiredCriticalHits &&
    (!Number.isSafeInteger(state.criticalHits) || state.criticalHits < requiredCriticalHits)
  ) {
    return { ok: false, reason: 'mastery-required', cost, level: level + 1, requiredCriticalHits };
  }
  return state.salvage >= cost
    ? { ok: true, key, cost, level: level + 1, requiredCriticalHits }
    : { ok: false, reason: 'insufficient-salvage', cost, requiredCriticalHits };
}
